import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { PortalBrand, applyPortalBrand } from "@/components/portal/PortalLayout";
import { Field } from "@/pages/portal/PortalLogin";
import { toast } from "sonner";

export default function PortalActivate() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("ref") || "email";
  const { activate } = usePortalAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState({});

  useEffect(() => { portalApi.get("/portal/branding").then((r) => setBranding(r.data || {})).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/portal/auth/invite/${token}`);
      setInvite(data);
      if (data.brand) applyPortalBrand(data.brand);
      if (data.already_active) navigate("/portal/login");
    } catch { setError("This invitation link is invalid or has expired."); }
    finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try { await activate(token, password, source); toast.success("Portal activated. Welcome!"); navigate("/portal/onboarding"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Activation failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0F1222]"><Icons.Loader2 className="h-7 w-7 animate-spin text-[#F5A623]" /></div>;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0F1222] p-6" data-testid="portal-activate-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><PortalBrand light logoUrl={invite?.brand?.logo_url || branding.firm_logo_url} firmName={invite?.brand?.name || branding.firm_name} /></div>
        {error ? (
          <div className="bg-[#171B2E] border border-white/10 rounded-2xl p-8 text-center">
            <Icons.AlertTriangle className="h-10 w-10 text-[#F5A623] mx-auto mb-3" />
            <p className="text-slate-300 text-sm">{error}</p>
            <button onClick={() => navigate("/portal/login")} className="mt-5 text-[#F5A623] text-sm hover:underline">Go to sign in</button>
          </div>
        ) : (
          <div className="bg-[#171B2E] border border-white/10 rounded-2xl p-8">
            <h1 className="font-heading text-2xl font-bold text-white">Set up your portal</h1>
            <p className="text-slate-400 text-sm mt-1 mb-6">Welcome, <span className="text-white font-medium">{invite?.name}</span>. Create a password for <span className="text-[#F5A623]">{invite?.email}</span>.</p>
            <form onSubmit={submit} className="space-y-4" data-testid="portal-activate-form">
              <Field icon="Lock" type="password" value={password} onChange={setPassword} placeholder="Create password (min 8 chars)" testid="portal-activate-password" />
              <Field icon="Lock" type="password" value={confirm} onChange={setConfirm} placeholder="Confirm password" testid="portal-activate-confirm" />
              <button type="submit" disabled={busy} data-testid="portal-activate-submit"
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.ShieldCheck className="h-4 w-4" />} Activate Portal
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
